/**
 * 解决方案页数据（批次二 · 农业运营与合规）
 *
 * 归属业务线：agriculture
 * 事实来源（hoyotech-farm 模块，均经源码/SQL 核实）：
 *   气象：FarmWeatherDataController / FarmWeatherInsightController / FarmWeatherProviderController /
 *         FarmWeatherDisasterAlertController / FarmWeatherDisasterRuleController（三级回退匹配）
 *   视频：FarmCameraController / FarmVideoControlController / FarmVideoStreamController /
 *         FarmVideoRecordingController + farm_video_storage_policy / farm_camera
 *         FarmEFenceController / FarmIntercomController / FarmSecurityAlertController
 *   农机资产：FarmMachineController / FarmMachineScheduleController + farm_machine_schedule
 *         FarmAssetController(+Category/Inventory/DepreciationReport) + farm_asset_cost_policy
 *         FarmAssetMaintenancePlanController / ReminderController / WorkOrderController
 *   库存：FarmWarehouseController / FarmWarehouseZoneController / FarmWarehouseLocationController（三级）
 *         FarmStockRecordController / FarmStockCheckController（+差异复核日志）/
 *         FarmStockExpireController / FarmStockPurchaseSuggestionController
 *   财务：FarmIncomeController / FarmExpenseController(+ExpenseApprovalRule) /
 *         FarmFinanceReportController / FarmFinanceAnalyticsController /
 *         FarmFinancePeriodController / FarmFinanceReconcileController
 *   合规：FarmRegulatorChannelController / FarmRegulatorReportController /
 *         FarmQualificationController / FarmQualificationExpireController + farm_qualification_expire_reminder
 *
 * 写作纪律：不写产量预测、不写「精准灌溉」（口径为「水肥一体/智能灌溉」）、不写无依据的数字。
 */
export const farmOpsSolutions = [
  /* ==================== 农业 · 气象与灾害 ==================== */
  {
    slug: 'farm-weather',
    line: 'agriculture',
    name: '农业气象墒情与灾害预警',
    en: 'Weather & Risk',
    eyebrow: 'Weather',
    tagline: '把外部天气，变成棚里能用的判断',
    summary:
      '多气象数据源接入、气象洞察、墒情联动与气象灾害预警规则。' +
      '重点不是显示天气，而是把天气变成灌溉、通风与防灾的判断依据。',
    overview:
      '棚内传感器只告诉你「现在棚里多少度」，但它解释不了「为什么」和「接下来会怎样」。' +
      '把外部气象接进来之后，判断才完整：明天有强降温就提前加保温，' +
      '连续阴雨就要防湿度过高诱发病害，大风预警就先把卷帘收好。' +
      '这类判断靠人盯天气预报容易漏，做成规则才稳定。',
    pains: [
      { title: '看天全靠手机天气 App', desc: '天气与棚内管理是两件事，没人负责把预警翻译成「现在该做什么」。' },
      { title: '灾害来时来不及反应', desc: '强降温、大风、暴雨往往提前几小时就能预知，但响应靠人记得才执行。' },
      { title: '气象数据源不可换', desc: '气象接口被写死在系统里，源不准或停服就得整体改。' }
    ],
    capabilities: [
      { icon: 'sun', title: '多气象数据源', desc: '气象数据源可配置，便于更换或并用，避免被单一数据源绑定。', tags: ['数据源可配', '可更换', '可并用'] },
      { icon: 'chart', title: '气象数据与洞察', desc: '展示气象要素并结合生产场景给出可读的洞察，而不只是罗列数字。', tags: ['气象要素', '场景洞察', '按需呈现'] },
      { icon: 'drop', title: '墒情联动', desc: '把气象与土壤墒情结合，为灌溉时机与通风策略提供依据。', tags: ['气象墒情', '灌溉参考', '通风参考'] },
      { icon: 'bell', title: '气象灾害预警', desc: '按灾害类型配置预警规则，命中后产生预警记录。', tags: ['灾害预警', '规则可配', '预警记录'] },
      { icon: 'gear', title: '三级回退匹配', desc: '灾害规则按「精确 → 同设备 → 语义」逐级回退匹配，避免规则配了不生效。', tags: ['三级回退', '精确匹配', '语义匹配'] },
      { icon: 'zap', title: '与规则引擎联动', desc: '预警可转为控制意图，由统一出口决定是否联动设备动作。', tags: ['联动控制', '统一出口', '避免抢设备'] }
    ],
    how: [
      { title: '接入气象数据源', desc: '选择与本地区匹配的气象源并验证准确性，数据源保持可更换。' },
      { title: '梳理灾害清单', desc: '列出本地实际会造成损失的气象灾害类型与阈值，不必求全。' },
      { title: '配置预警规则', desc: '按灾害类型建规则，并明确预警产生后由谁负责、做什么动作。' },
      { title: '与设备联动', desc: '把适合自动化的部分接入规则引擎，敏感操作保留人工确认。' },
      { title: '复盘校准', desc: '每个生长季复盘一次漏报与误报，调整阈值与动作。' }
    ],
    specs: [
      { k: '气象源', v: '气象数据提供方可配置，支持更换与并用' },
      { k: '灾害规则', v: '按灾害类型建立预警规则，命中后产生预警记录' },
      { k: '匹配方式', v: '灾害规则按「精确 → 同设备 → 语义」三级回退匹配，降低规则不命中的概率' },
      { k: '联动口径', v: '预警可转为控制意图，实际下发仍由统一出口执行，避免多入口抢设备' },
      { k: '现场确认项', v: '本地灾害类型、阈值与应急动作需结合生产经验确定' }
    ],
    faq: [
      { q: '气象数据为什么要可更换？', a: '因为气象源的准确率与可用性会变，而且不同地区适合的源不一样。如果接口写死在系统里，换源就要改代码；做成可配置后，换源是配置动作，也不怕某一家停服。' },
      { q: '「三级回退匹配」是什么意思？', a: '灾害规则匹配时，先按精确条件找，找不到再放宽到同设备范围，仍找不到则按语义匹配。目的是减少「规则明明配了却不生效」这种情况——这在灾害预警里代价很高。' },
      { q: '预警能自动控制设备吗？', a: '可以转为控制意图，但实际下发由统一出口执行，避免预警、定时任务与人工操作三方抢设备。对风险较高的动作（如大规模卷帘、启动加温）建议保留人工确认。' },
      { q: '和棚内环境监测是一回事吗？', a: '不是。环境监测看的是棚内实时状态，气象看的是棚外与未来趋势。两者结合后判断才完整，所以建议在同一套系统里用，避免两边数据脱节。' }
    ],
    related: ['environment-monitoring', 'farm-video-security']
  },

  /* ==================== 农业 · 视频安防 ==================== */
  {
    slug: 'farm-video-security',
    line: 'agriculture',
    name: '农业视频安防与电子围栏',
    en: 'Video & Security',
    eyebrow: 'Security',
    tagline: '看的不是画面，是「什么时候、哪里、出了什么事」',
    summary:
      '摄像头接入、实时预览与控制、录像与存储策略、电子围栏与安防告警，' +
      '并与设备告警、对讲能力放在同一套视图里。',
    overview:
      '农业园区的安防很分散：山地地块、连栋大棚、养殖圈舍、水产塘口往往相距很远，' +
      '人巡不过来。视频本身不是目的——真正要解决的是「有人进入禁入区」' +
      '「夜里某个棚有异常」这类事件，能不能被自动发现并留下可查的记录。',
    pains: [
      { title: '摄像头装了不少，没人看', desc: '录像在存，但没有事件触发，出事只能事后翻录像，翻不到就只能认。' },
      { title: '重点区域防不住', desc: '水源地、饲料间、种苗区等禁入区域靠人巡，夜里和农忙季最容易出问题。' },
      { title: '视频和设备告警两套系统', desc: '安防和设备的告警各自推，判断「同一时间发生了什么」要来回切。' }
    ],
    capabilities: [
      { icon: 'play', title: '摄像头接入与管理', desc: '摄像头建档并纳入统一管理，支持按区域与用途组织。', tags: ['摄像头台账', '分区管理', '统一入口'] },
      { icon: 'activity', title: '实时预览与控制', desc: '实时查看画面并对可控设备执行相应操作，减少现场跑动。', tags: ['实时预览', '远程控制', '减少跑现场'] },
      { icon: 'file', title: '录像与存储策略', desc: '录像留存，存储策略可按需配置，兼顾留存时长与成本。', tags: ['录像留存', '存储策略', '成本可控'] },
      { icon: 'shield', title: '电子围栏', desc: '对重点区域设置电子围栏，越界产生安防告警。', tags: ['越界告警', '重点区域', '自动化'] },
      { icon: 'bell', title: '安防告警', desc: '安防事件与设备告警在同一套视图里，便于还原现场全貌。', tags: ['安防事件', '统一视图', '事件还原'] },
      { icon: 'users2', title: '现场对讲', desc: '支持与现场对讲，远程即可确认情况或做简单指挥。', tags: ['远程对讲', '现场确认', '减少误判'] }
    ],
    how: [
      { title: '确定重点区域', desc: '先圈出真正需要盯的区域（水源、饲料间、种苗区、出入口），不必全场无差别布防。' },
      { title: '接入摄像头', desc: '按区域接入并建档，确认画面覆盖与夜间效果满足要求。' },
      { title: '设置电子围栏', desc: '对禁入区域划设围栏，先跑一段时间看误报率。' },
      { title: '配置告警与责任人', desc: '明确安防告警由谁接收、如何处理、如何记录。' },
      { title: '与其他告警合并查看', desc: '把安防与设备告警放在同一时间轴上，还原事件更准确。' }
    ],
    specs: [
      { k: '视频能力', v: '支持摄像头接入、实时预览与控制、录像留存与存储策略配置' },
      { k: '安防能力', v: '支持电子围栏、安防告警与现场对讲' },
      { k: '告警归集', v: '安防事件与设备告警可在统一视图中查看，便于还原现场' },
      { k: '存储策略', v: '录像存储策略可配置，按留存需求与成本权衡' },
      { k: '现场确认项', v: '点位覆盖、夜间效果与网络条件需现场勘查确认' }
    ],
    faq: [
      { q: '园区没有稳定网络，能上视频吗？', a: '视频对上行带宽要求比传感器高得多。可行做法是只在关键区域上视频、本地存录像、只把告警与截图上传，具体方案要按现场网络条件评估。' },
      { q: '电子围栏会不会天天误报？', a: '取决于划设的区域和画面质量。建议先圈最小范围的重点区域（而不是整块地）跑一段时间，根据实际误报再调整，不要一上来全场布防。' },
      { q: '视频和传感器告警要两套系统吗？', a: '不需要，也不建议。分开之后最麻烦的是判断「同一时间发生了什么」——比如设备告警的那一刻现场是不是有人。放在同一套视图里，这类问题才答得清楚。' },
      { q: '录像要留多久？', a: '看用途：用于纠纷取证通常需要更长留存，用于日常巡看则短留存即可。录像占用空间大，建议按区域分级设置留存策略，不要一刀切。' }
    ],
    related: ['farm-weather', 'device-control']
  },

  /* ==================== 农业 · 农机与资产 ==================== */
  {
    slug: 'farm-machine-asset',
    line: 'agriculture',
    name: '农机调度与资产维保',
    en: 'Machinery & Assets',
    eyebrow: 'Assets',
    tagline: '农忙季里，谁用哪台机器得说清楚',
    summary:
      '农机档案与作业排程、资产台账与分类、折旧与成本策略、资产盘点，' +
      '以及维保计划、到期提醒与维保工单。',
    overview:
      '农业的资产有两类麻烦：一类是农忙季集中抢机器，谁排到谁没排到容易起争执；' +
      '另一类是资产数量多、分布散，账上有、地里找不到，或者坏了没人管。' +
      '这两件事都指向同一个要求——资产和机械的每一次占用、每一次维保都要有记录。',
    pains: [
      { title: '农忙季排机靠喊', desc: '同一台收割机好几家在等，谁先谁后靠人情，排了也可能临时变。' },
      { title: '账实不符', desc: '资产台账与现场对不上，盘点一次要停几天工。' },
      { title: '维保没人提醒', desc: '机械该保养了没人知道，等到农忙季掉链子。' }
    ],
    capabilities: [
      { icon: 'factory', title: '农机档案与排程', desc: '农机建档后按作业安排排程，减少农忙季的临时协调成本。', tags: ['农机台账', '作业排程', '忙季调度'] },
      { icon: 'box', title: '资产台账与分类', desc: '资产按类别建档，明确位置、责任人与状态。', tags: ['资产台账', '分类管理', '责任到人'] },
      { icon: 'chart', title: '折旧与成本策略', desc: '按折旧规则与成本策略核算资产成本，为更新决策提供依据。', tags: ['折旧核算', '成本策略', '更新依据'] },
      { icon: 'clipboard', title: '资产盘点', desc: '按计划盘点并记录差异，减少账实不符。', tags: ['定期盘点', '差异记录', '账实一致'] },
      { icon: 'clock', title: '维保计划与提醒', desc: '按周期生成维保计划并提醒，避免农忙季才发现该保养。', tags: ['维保计划', '到期提醒', '降低故障'] },
      { icon: 'gear', title: '维保工单闭环', desc: '维保以工单形式派发与回填，责任与结果都可追溯。', tags: ['维保工单', '闭环管理', '可追溯'] }
    ],
    how: [
      { title: '建立资产与农机台账', desc: '先把资产与农机清点建档，明确位置与责任人。' },
      { title: '设定折旧与成本规则', desc: '按资产类别设定折旧方式与成本归集口径。' },
      { title: '编排作业计划', desc: '把农忙季的作业需求提前排成计划，减少临时争抢。' },
      { title: '生成维保计划', desc: '按周期或使用情况生成维保任务，并指定执行人。' },
      { title: '盘点与复盘', desc: '定期盘点核对账实，并用维保记录判断哪些资产该更新。' }
    ],
    specs: [
      { k: '农机管理', v: '农机档案与作业排程独立管理，支撑忙季调度' },
      { k: '资产核算', v: '支持资产分类、盘点、折旧报表与资产成本策略' },
      { k: '维保闭环', v: '维保计划、到期提醒与维保工单三段打通，工单可回填结果' },
      { k: '数据一致性', v: '资产、机械与维保记录互相引用，避免三本账' },
      { k: '现场确认项', v: '资产清单、折旧口径与维保周期需按实际管理要求设定' }
    ],
    faq: [
      { q: '我们资产就是些小农具，需要系统吗？', a: '看规模。资产少、价值低的话用表格管也行。真正值得上系统的是「数量多 + 分布散 + 有人共用」的情形——这时候账实不符和排用争执造成的损失通常比工具成本高。' },
      { q: '折旧这块和我们财务口径能对上吗？', a: '折旧方式与成本策略是可配置的，但**具体口径需要与你们财务确认后设定**，我们不会预设一套「标准算法」硬套。建议实施时让财务一起参与这一步。' },
      { q: '农忙季排程能自动排吗？', a: '这套提供的是作业排程管理（记录谁在什么时间用哪台机械），不是自动排产算法。对农业而言，人工排 + 系统留痕通常比算法更贴合实际，因为地块条件和天气变数太大。' },
      { q: '维保提醒发到哪里？', a: '可以在系统内形成待办，也可以配置成告警推送给责任人。建议先发给人，跑顺后再考虑与设备状态联动。' }
    ],
    related: ['device-access', 'four-business-types']
  },

  /* ==================== 农业 · 库存与货位 ==================== */
  {
    slug: 'farm-inventory',
    line: 'agriculture',
    name: '农产品库存与三级货位',
    en: 'Inventory',
    eyebrow: 'Inventory',
    tagline: '仓库—货区—货位三级，找得到才算管得住',
    summary:
      '仓库、货区、货位三级结构，配合库存流水、盘点与差异复核、效期预警与采购建议。',
    overview:
      '农产品的库存比一般商品更麻烦：有季节集中的入库高峰、有保质期、' +
      '还有「同一批货分在几个仓」的情况。只记「有多少」不够，' +
      '还要能回答「在哪一格、是哪一批、还有多久过期」。这就是三级货位加批次管理的意义。',
    pains: [
      { title: '只知道有货，不知道在哪', desc: '账面有数、现场找不到，发货靠人翻。' },
      { title: '过期才发现', desc: '没有效期预警，临期或过期商品在盘点时才暴露。' },
      { title: '盘点差异说不清', desc: '盘完和账面不符，但差异原因没有记录与复核，下次继续不符。' }
    ],
    capabilities: [
      { icon: 'layers', title: '仓库·货区·货位三级', desc: '库位结构到货位粒度，出入库可定位到具体位置。', tags: ['三级结构', '精确定位', '找货快'] },
      { icon: 'file', title: '库存流水', desc: '每笔出入库形成流水，可回溯到时间、单据与经办人。', tags: ['出入库流水', '可回溯', '单据关联'] },
      { icon: 'clipboard', title: '盘点与差异复核', desc: '盘点差异进入复核流程，记录原因与处理，避免重复出错。', tags: ['盘点', '差异复核', '原因留痕'] },
      { icon: 'bell', title: '效期预警', desc: '临期与过期预警，给处理留出时间窗口。', tags: ['效期预警', '临期提醒', '减少报损'] },
      { icon: 'chart', title: '采购建议', desc: '结合库存与消耗给出采购建议，避免一边积压一边缺货。', tags: ['采购建议', '减少积压', '避免断货'] },
      { icon: 'box', title: '批次管理', desc: '按批次管理库存，与溯源链路自然衔接。', tags: ['批次管理', '与溯源衔接', '可追溯'] }
    ],
    how: [
      { title: '规划库位结构', desc: '先按实际仓库划分货区与货位，结构要与现场物理位置一致。' },
      { title: '建立商品与批次档案', desc: '商品及批次建档，明确单位、效期与存储要求。' },
      { title: '规范出入库操作', desc: '要求出入库都走系统并定位到货位，这是数据能用的前提。' },
      { title: '跑通盘点流程', desc: '定期盘点并把差异纳入复核流程，记录原因。' },
      { title: '用数据管采购', desc: '用库存与消耗数据支撑采购决策，逐步减少积压与断货。' }
    ],
    specs: [
      { k: '库位结构', v: '仓库、货区、货位三级结构，出入库可定位到货位粒度' },
      { k: '流水与盘点', v: '库存流水可回溯；盘点支持差异记录与复核流程' },
      { k: '效期管理', v: '支持效期与临期预警，预警记录可查' },
      { k: '采购辅助', v: '提供采购建议，减少积压与断货同时发生的概率' },
      { k: '与溯源衔接', v: '库存按批次管理，可与溯源链路衔接' }
    ],
    faq: [
      { q: '我们仓库很小，需要三级货位吗？', a: '不一定需要三级。库位结构可以按实际规模裁剪——只有一个仓、一个区的话，直接用货位即可。三级结构的价值在于「货多、区多、人多个」的时候还能找得到。' },
      { q: '农产品按批次管，会不会很难操作？', a: '入库时多记一个批次号即可，之后的好处很实在：能查这批货什么时候进的、还能放多久、对应哪块地的产出。这一步同时也是溯源链路的起点，算一次投入两处收益。' },
      { q: '盘点差异复核有必要吗？', a: '很有必要。差异本身不可怕，可怕的是每次差异都「就这么算了」——原因不记录，同样的错会一直犯。复核流程的作用就是把差异变成可改进的信息。' },
      { q: '能自动算该采购多少吗？', a: '系统会结合库存与消耗给出采购建议，但建议不等于结论——农产品的采购还受产季、价格和订单影响，建议作为参考，最终由人判断。' }
    ],
    related: ['farm-finance', 'traceability']
  },

  /* ==================== 农业 · 财务经营 ==================== */
  {
    slug: 'farm-finance',
    line: 'agriculture',
    name: '农业财务与经营分析',
    en: 'Finance',
    eyebrow: 'Finance',
    tagline: '把「这季到底赚没赚」算清楚',
    summary:
      '收入与支出记账、支出审批规则、会计期间、财务报表、经营分析与对账。' +
      '目标是让每一季、每一块地的投入产出能算得清。',
    overview:
      '农业经营决策最缺的往往不是产量数据，而是「花了多少、赚了多少、哪一块地在亏」。' +
      '因为投入分散在人工、农资、机械、水电上，而产出又受价格波动影响，' +
      '不用系统归集就只能靠年底估。把收支与生产数据挂在一起之后，' +
      '「哪种作物、哪个地块值得继续投」才有依据。',
    pains: [
      { title: '年底才算一次总账', desc: '缺乏期间归集，过程里看不到经营状况，发现亏损往往已经晚了。' },
      { title: '支出没有审批约束', desc: '费用支出随口点头，事后对不上账，也说不清花在了哪块地。' },
      { title: '投入产出对不上地块', desc: '成本没按地块或作物归集，无法判断哪一块地该保留、哪一块该调整。' }
    ],
    capabilities: [
      { icon: 'chart', title: '收入管理', desc: '按来源登记收入，支撑按作物、地块与期间的收入归集。', tags: ['收入登记', '按来源', '期间归集'] },
      { icon: 'file', title: '支出与审批规则', desc: '支出登记并支持审批规则约束，让费用发生有据可依。', tags: ['支出登记', '审批规则', '过程可控'] },
      { icon: 'clock', title: '会计期间', desc: '按会计期间归集收支，支持过程性查看而非年底一次性结算。', tags: ['期间归集', '过程可视', '按期结账'] },
      { icon: 'clipboard', title: '财务报表', desc: '生成经营报表，把收支结构与趋势呈现出来。', tags: ['经营报表', '结构呈现', '趋势对比'] },
      { icon: 'activity', title: '经营分析', desc: '结合生产与成本数据做经营分析，支撑种植结构与投入调整。', tags: ['经营分析', '结构调整', '决策依据'] },
      { icon: 'check', title: '对账', desc: '支持对账流程，减少账目长期挂账与差异不清。', tags: ['对账流程', '减少挂账', '差异可查'] }
    ],
    how: [
      { title: '确定归集口径', desc: '先明确按地块、按作物还是按基地归集成本，口径要一次定清楚。' },
      { title: '设置审批规则', desc: '按金额或费用类型设定审批规则，避免口头上点头。' },
      { title: '规范录入', desc: '要求收支发生即登记，这是所有分析的前提。' },
      { title: '按期出报表', desc: '按会计期间出经营报表，及时发现问题而不是年底才发现。' },
      { title: '做经营对比', desc: '用几期的数据横向对比地块与作物，调整投入结构。' }
    ],
    specs: [
      { k: '收支管理', v: '收入与支出分别管理，支出支持审批规则约束' },
      { k: '期间与报表', v: '按会计期间归集，提供财务报表与经营分析' },
      { k: '对账能力', v: '支持对账流程，减少账目长期不清' },
      { k: '与生产衔接', v: '可与生产、库存数据结合，支撑按地块/作物的投入产出分析' },
      { k: '现场确认项', v: '**归集口径与会计科目需与你们财务确认后设定**，我们不预设标准' }
    ],
    faq: [
      { q: '这套能替代我们现有的财务软件吗？', a: '不建议这么理解。这套的价值在于**把成本按地块、作物归集**，从而支撑经营决策；而规范的账务处理、报税通常仍由专业财务软件完成。两者可以并存，关键是口径要对齐。' },
      { q: '我们没有专职财务，能上吗？', a: '可以，但要降低预期动作——先只登记两类数据：大额支出和销售收入，按地块记。跑一季之后你会得到一份「哪块地赚钱」的对照表，这已经比年底估账强很多。' },
      { q: '会计期间和自然月一样吗？', a: '可以按自然月，也可以按你们的结账习惯设置。对农业来说，**按作物季设置期间**往往比按自然月更贴合经营判断。具体怎么设需与财务确认。' },
      { q: '能和库存、销售数据打通吗？', a: '可以。同一套平台里库存、销售与财务的数据是通的，这也是把财务放在同一平台而不是独立系统的意义——否则每次算投入产出都要人工拼表。' }
    ],
    related: ['farm-compliance', 'farm-inventory']
  },

  /* ==================== 农业 · 监管与资质 ==================== */
  {
    slug: 'farm-compliance',
    line: 'agriculture',
    name: '监管报送与资质到期管理',
    en: 'Compliance',
    eyebrow: 'Compliance',
    tagline: '别等到要报送了才开始凑数据',
    summary:
      '监管渠道管理、报送报表，加上资质台账与到期提醒。' +
      '把「临时凑材料」变成「随时能导出」。',
    overview:
      '农业企业的合规工作有两个固定痛点：一是监管报送往往临时通知、' +
      '要求的数据散在不同人手里，报送前要加班凑；二是各种资质证照到期时间不一，' +
      '常常是用到的时候才发现已经过期。这两件事都不难，难在没人持续盯着。',
    pains: [
      { title: '报送前突击凑数', desc: '平时不整理，限期一到就四处找人要数据，质量也难保证。' },
      { title: '资质到期没人盯', desc: '证照分属不同人管，到期时间不一，业务受影响才发现。' },
      { title: '数据口径不一致', desc: '对外报送的数与内部台账对不上，解释起来很被动。' }
    ],
    capabilities: [
      { icon: 'link', title: '监管渠道管理', desc: '管理对外的监管渠道与报送对象，明确报送要求与周期。', tags: ['渠道台账', '报送要求', '周期明确'] },
      { icon: 'file', title: '报送报表', desc: '按要求归集并生成报送报表，减少临时拼数。', tags: ['数据归集', '报表生成', '减少突击'] },
      { icon: 'clipboard', title: '资质台账', desc: '统一登记各类资质证照，明确持有人与有效期。', tags: ['资质台账', '有效期', '责任人'] },
      { icon: 'bell', title: '到期提醒', desc: '资质到期前自动提醒，避免因证照过期影响业务。', tags: ['到期提醒', '提前预警', '避免中断'] },
      { icon: 'check', title: '数据一致性', desc: '报送数据来源与内部台账一致，避免对外口径打架。', tags: ['口径一致', '内部对齐', '减少解释成本'] },
      { icon: 'chart', title: '合规概览', desc: '集中查看待办报送与临期资质，让合规工作有清单可依。', tags: ['合规概览', '待办清单', '不漏项'] }
    ],
    how: [
      { title: '梳理监管要求', desc: '列出需要对接的监管渠道、报送内容与时间要求。' },
      { title: '登记资质台账', desc: '把所有证照登记进台账，标注有效期与责任人。' },
      { title: '设置提醒', desc: '按提前量配置到期提醒，留出办理时间。' },
      { title: '沉淀报送数据', desc: '把日常生产与经营数据规范录入，报送时直接归集。' },
      { title: '定期核对', desc: '定期核对报送口径与内部台账，避免两边不一致。' }
    ],
    specs: [
      { k: '监管对接', v: '监管渠道与报送报表独立管理，可按要求扩展' },
      { k: '资质管理', v: '资质台账与到期提醒配套，提醒记录可查' },
      { k: '数据来源', v: '报送数据来自平台内的生产与经营数据，避免另做一套' },
      { k: '扩展方式', v: '新增监管渠道以配置为主，不需要为每家渠道改系统' },
      { k: '现场确认项', v: '**具体对接的监管平台与报送接口需按项目确认**，不同地区要求不一' }
    ],
    faq: [
      { q: '能自动对接到监管平台吗？', a: '要看具体是哪个监管平台。这套提供了监管渠道与报送报表的管理能力，是否支持直连上报、是否需要按对方接口定制开发，**需要按项目确认**——不同地区、不同平台的要求差别很大，我们不会预先承诺。' },
      { q: '资质提醒发到哪里？', a: '可以在系统内形成待办，也可以推送给责任人。建议至少留两个提前量（比如到期前 60 天和 30 天各提醒一次），因为换证往往需要准备材料和时间。' },
      { q: '报送数据和内部台账怎么保证一致？', a: '关键是不要另做一套。报送报表从平台内的生产经营数据归集，而不是让人重新填一遍。这样口径天然一致，也省了重复劳动。' },
      { q: '我们资质不多，需要这个模块吗？', a: '资质不多的话，一张表加日历提醒也能管。这个模块的价值在资质与报送项数多、经手人多的时候才明显——那时容易漏，而漏一次可能要重办。' }
    ],
    related: ['four-business-types', 'farm-finance']
  }
]
